import React, { FC } from "react";

import { Button } from "@/components/Button";
import { Header } from "@/components/Header";
import { Icon } from "@/components/Icon";
import { Text } from "@/components/Text";
import { Flex, IDialogModal, inDialogModal } from "@/eworkitComponents";
import { TColorVariant } from "@/style/colorVariants";
import { CloseButton } from "@/views/components/CloseButton";
import { StyledModal } from "@/views/components/Modal";

interface IConfirmationDialogProps extends IDialogModal {
  onAccept: (close: () => void) => void;
  onCancel?: () => void;
  header: string;
  icon?: string;
  iconColor?: TColorVariant;
  confirmationText: string;
  acceptButtonLabel: string;
  cancelButtonLabel: string;
  hasCancelButton?: boolean;
  uppercaseButtonLabel?: boolean;
}

const ConfirmationDialog: FC<IConfirmationDialogProps> = ({
  header,
  onClose,
  icon,
  iconColor = "error",
  confirmationText,
  acceptButtonLabel,
  onAccept,
  cancelButtonLabel,
  onCancel,
  children,
  hasCancelButton = true,
  uppercaseButtonLabel = true,
}) => {
  const handleCancel = () => {
    onClose();
    onCancel?.();
  };

  const handleAccept = () => {
    onAccept(onClose);
  };

  return (
    <StyledModal>
      <Flex direction="column" padding={["lg"]} innerMargin="lg">
        <Flex justify="space-between">
          <Header weight="bold">{header}</Header>
          <CloseButton onClick={handleCancel} testName="close" />
        </Flex>
        {icon && (
          <Flex justify="center">
            <Icon icon={icon} size="xxxl" color={iconColor} />
          </Flex>
        )}
        <Flex direction="column" innerMargin="lg">
          <Text>{confirmationText}</Text>

          {children}

          <Flex justify="flex-end" innerMargin="md">
            {hasCancelButton && (
              <Button variant="secondary" onClick={handleCancel} size="lg" testName="cancel">
                <Text uppercase={uppercaseButtonLabel}>{cancelButtonLabel}</Text>
              </Button>
            )}
            <Button onClick={handleAccept} size="lg" testName="delete">
              <Text uppercase={uppercaseButtonLabel}>{acceptButtonLabel}</Text>
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </StyledModal>
  );
};

const ConfirmationDialogModalComponent = inDialogModal(ConfirmationDialog);

export interface IConfirmationModalProps extends IConfirmationDialogProps {}

export const ConfirmationDialogModal: FC<IConfirmationModalProps> = props => {
  return <ConfirmationDialogModalComponent size="xs" {...props} />;
};
